import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { useCallback } from "preact/hooks";
import { useIsMobile } from "../../../shared/hooks/useIsMobile";
import { lerp, clamp } from "../../../shared/utils/interpolation";

export interface SpineFitOptions {
  leftPercent?: number;
  topOffset?: number;
  scale?: number;
}

export const useSpineHelpers = () => {
  const isMobile = useIsMobile();
  const isLarge = useIsMobile({ breakpoint: 1920, compare: "gte" });
  const opts = isMobile
    ? { leftPercent: 0.3, topOffset: 1.1, scale: 1 }
    : isLarge
      ? { leftPercent: 0.29, topOffset: 0.6, scale: 1 }
      : { leftPercent: 0.4, topOffset: 0.6, scale: 1 };

  const fitAndPosition = useCallback(
    (spine: Spine, w: number, h: number) => {
      const { leftPercent, topOffset, scale } = opts;
      spine.scale.set(scale);
      const x = w * leftPercent;
      const y = topOffset * h;
      spine.position.set(0, 0);
    },
    [opts.leftPercent, opts.topOffset, opts.scale]
  );

  return {
    fitAndPosition,
  };
};

export const PARALLAX_MAX_MULTIPLIER = 60;
export const PARALLAX_TICK_SIZE_TIME = 200;
export const PARALLAX_MAX_OFFSET_Y = 4300;

export interface ParallaxRefs {
  spineRef: { current: Spine | null };
  basePositionYRef: { current: number | null };
  interpolatedMultiplierRef: { current: number };
  prevMultiplierRef: { current: number };
  targetMultiplierRef: { current: number };
  tStartRef: { current: number | null };
  animationFrameRef: { current: number | null };
  isAnimatingRef: { current: boolean };
}

interface ParallaxOptions {
  maxMultiplier?: number;
  tickSizeTime?: number;
  maxOffsetY?: number;
}

export const applyParallaxStep = (
  refs: ParallaxRefs,
  options?: ParallaxOptions
): boolean => {
  const spine = refs.spineRef.current;
  if (!spine || refs.basePositionYRef.current === null) return false;

  const maxMultiplier = options?.maxMultiplier ?? PARALLAX_MAX_MULTIPLIER;
  const tickSizeTime = options?.tickSizeTime ?? PARALLAX_TICK_SIZE_TIME;
  const maxOffsetY = options?.maxOffsetY ?? PARALLAX_MAX_OFFSET_Y;

  const currentTime = Date.now();
  const tNormalized = clamp(
    (currentTime - (refs.tStartRef.current ?? currentTime)) / tickSizeTime,
    0,
    1
  );

  refs.interpolatedMultiplierRef.current = lerp(
    refs.prevMultiplierRef.current,
    refs.targetMultiplierRef.current,
    tNormalized
  );

  const safeMultiplier = Math.max(refs.interpolatedMultiplierRef.current, 1);
  const normalizedMultiplier = clamp(
    Math.log(safeMultiplier) / Math.log(maxMultiplier),
    0,
    1
  );

  const offsetY = normalizedMultiplier * maxOffsetY;

  spine.position.y = refs.basePositionYRef.current + offsetY;

  if (tNormalized < 1) {
    return true;
  }

  refs.isAnimatingRef.current = false;
  return false;
};

export const startParallaxAnimation = (
  refs: ParallaxRefs,
  applyParallax: () => void,
  targetMultiplier: number
) => {
  if (refs.isAnimatingRef.current) {
    if (refs.animationFrameRef.current !== null) {
      cancelAnimationFrame(refs.animationFrameRef.current);
    }
  }

  refs.prevMultiplierRef.current = refs.interpolatedMultiplierRef.current;
  refs.targetMultiplierRef.current = targetMultiplier;
  refs.tStartRef.current = Date.now();
  refs.isAnimatingRef.current = true;

  refs.animationFrameRef.current = requestAnimationFrame(applyParallax);
};
